import React, { useEffect, useState, useRef } from "react";
import { getHoldings, syncTransactions, getTransactionsByAccount } from "../../api/plaidClient";
import TransactionList from "./TransactionList";
import { formatCurrency, formatAccountSubtype } from "../../utils/formatters";

function isValidTicker(ticker) {
    return typeof ticker === "string" && /^[A-Z0-9]{1,5}$/.test(ticker);
}

export default function AccountDetail({ account, onBack }) {
    const [detailData, setDetailData] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [errorDetail, setErrorDetail] = useState(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        async function fetchDetail() {
            setLoadingDetail(true);
            setErrorDetail(null);

            try {
                const subtype = account.subtype.toUpperCase();

                if (subtype === "IRA" || subtype === "_401K") {
                    const allHoldings = await getHoldings(signal);
                    const myHoldings = allHoldings.filter(
                        (h) => h.accountId === account.accountId
                    );
                    setDetailData({ type: "investment", holdings: myHoldings });
                } else {
                    await syncTransactions(signal);
                    const databaseTransactions = await getTransactionsByAccount(account.accountId, signal);
                    setDetailData({ type: "bank", transactions: databaseTransactions });
                }
            } catch (e) {
                if (e.name === 'AbortError' || e.name === 'CanceledError') {
                    return;
                }
                console.error("Error fetching detail:", e);
                setErrorDetail("Could not load account details.");
            } finally {
                setLoadingDetail(false);
            }
        }

        fetchDetail();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [account]);

    const displaySubtype = formatAccountSubtype(account.subtype);

    const displayHoldings =
        detailData && detailData.type === "investment"
            ? detailData.holdings.filter((h) => h.name !== "U S Dollar")
            : [];

    return (
        <div>
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-6"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Portfolio
            </button>

            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
                <p className="text-gray-500 mt-1">{displaySubtype}</p>
            </div>

            {loadingDetail && (
                <div className="card p-6">
                    <div className="flex items-center gap-3">
                        <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full" />
                        <span className="text-gray-500">Loading details...</span>
                    </div>
                </div>
            )}

            {errorDetail && (
                <div className="card p-6">
                    <p className="text-danger-500">{errorDetail}</p>
                </div>
            )}

            {/* Bank Accounts: Transaction List */}
            {!loadingDetail && detailData && detailData.type === "bank" && (
                <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h2>
                    <TransactionList transactions={detailData.transactions} accountType={account.type} />
                </div>
            )}

            {/* Investment Accounts: Holdings Table */}
            {!loadingDetail && detailData && detailData.type === "investment" && (
                <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Holdings</h2>

                    {displayHoldings.length === 0 ? (
                        <p className="text-gray-500">No holdings found for this account.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Ticker</th>
                                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Name</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Quantity</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Cost/Share</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Cost Basis</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Price</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Gain/Loss</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayHoldings.map((h) => {
                                        const displayTicker = isValidTicker(h.tickerSymbol)
                                            ? h.tickerSymbol
                                            : h.name;

                                        return (
                                            <tr key={h.securityId} className="border-b border-gray-200">
                                                <td className="py-3 text-gray-900 font-medium">
                                                    {displayTicker || h.securityId}
                                                </td>
                                                <td className="py-3 text-gray-500">
                                                    {h.name || "<unknown>"}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {h.quantity}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.costShare)}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.costBasis)}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.closePrice)}
                                                </td>
                                                <td className={`py-3 text-right font-medium ${h.gainLoss >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                                                    {formatCurrency(h.gainLoss)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
